import { MediaDeviceFailure, extractConstraintsWithKeys, findMediaInputFromMediaStreamTrack, getUserMedia, isExactDeviceConstraint, isStreamingRequestedDevices, relaxInputConstraint, } from '@pexip/media-control';
import { assert } from '@pexip/utils';
import { MediaInputStatusFlag, InputStatusMask, createUserMediaStatus, formatUserMediaStatus, replaceInputStatus, } from './types';
import { buildMedia, createMediaTrack, findExpectedInput } from './utils';
import { logger } from './logger';
import { getPermissionStatus, isUnknownError } from './status';
import { PROCESSOR_LABELS } from './constants';
const toAcquiredInputStatus = (matched) => MediaInputStatusFlag.Requested |
    MediaInputStatusFlag.PermissionGranted |
    MediaInputStatusFlag.DeviceAvailable |
    MediaInputStatusFlag.TrackPresent |
    (matched ? MediaInputStatusFlag.ExactMatch : MediaInputStatusFlag.Fallback);
const toUnrequestedInputStatus = (kind, devices) => {
    if (devices.size(kind) === 0) {
        return MediaInputStatusFlag.NoDevice;
    }
    return MediaInputStatusFlag.DeviceAvailable;
};
export const deriveSuccessStatus = (constraints, matched, devices) => createUserMediaStatus({
    audio: constraints.audio
        ? toAcquiredInputStatus(matched.audio)
        : toUnrequestedInputStatus('audioinput', devices),
    video: constraints.video
        ? toAcquiredInputStatus(matched.video)
        : toUnrequestedInputStatus('videoinput', devices),
});
const toInputErrorStatus = (flag) => MediaInputStatusFlag.Requested |
    (flag === MediaInputStatusFlag.PermissionDenied ||
        flag === MediaInputStatusFlag.NoDevice
        ? MediaInputStatusFlag.None
        : MediaInputStatusFlag.DeviceAvailable) |
    flag;
const createErrorStatus = (flag, requested) => createUserMediaStatus({
    audio: requested.audio
        ? toInputErrorStatus(flag)
        : MediaInputStatusFlag.None,
    video: requested.video
        ? toInputErrorStatus(flag)
        : MediaInputStatusFlag.None,
});
/**
 * Resolves the standardized media failure represented by an error.
 *
 * @example
 * getMediaDeviceFailure(new DOMException('', 'NotAllowedError'));
 */
const getMediaDeviceFailure = (error) => {
    const failures = Object.values(MediaDeviceFailure);
    if (failures.includes(error.name)) {
        return error.name;
    }
    if (failures.includes(error.message)) {
        return error.message;
    }
};
/**
 * Creates a terminal status when requested inputs have no available devices.
 *
 * @example
 * deriveNoDeviceStatus({audio: true, video: false});
 */
export const deriveNoDeviceStatus = (constraints) => createErrorStatus(MediaInputStatusFlag.NoDevice, {
    audio: Boolean(constraints.audio),
    video: Boolean(constraints.video),
});
export const deriveErrorStatus = (error, constraints) => {
    const requested = {
        audio: Boolean(constraints.audio),
        video: Boolean(constraints.video),
    };
    const requestedOrBoth = requested.audio || requested.video
        ? requested
        : { audio: true, video: true };
    const failure = getMediaDeviceFailure(error);
    switch (failure) {
        case MediaDeviceFailure.AudioInputDeviceNotFoundError:
            return createErrorStatus(MediaInputStatusFlag.RequestedDeviceNotFound, { audio: true, video: false });
        case MediaDeviceFailure.VideoInputDeviceNotFoundError:
            return createErrorStatus(MediaInputStatusFlag.RequestedDeviceNotFound, { audio: false, video: true });
        case MediaDeviceFailure.AudioAndVideoDeviceNotFoundError:
            return createErrorStatus(MediaInputStatusFlag.RequestedDeviceNotFound, { audio: true, video: true });
        case MediaDeviceFailure.NotAllowedError:
        case MediaDeviceFailure.SecurityError:
        case MediaDeviceFailure.PermissionDeniedError:
            return createErrorStatus(MediaInputStatusFlag.PermissionDenied, requestedOrBoth);
        case MediaDeviceFailure.NotReadableError:
        case MediaDeviceFailure.TrackStartError:
        case MediaDeviceFailure.AbortError:
            return createErrorStatus(MediaInputStatusFlag.DeviceInUse, requestedOrBoth);
        case MediaDeviceFailure.MissingConstraintsError:
        case MediaDeviceFailure.NotFoundError:
            return deriveNoDeviceStatus(requestedOrBoth);
        case MediaDeviceFailure.OverconstrainedError:
            return createErrorStatus(MediaInputStatusFlag.Overconstrained, requestedOrBoth);
        case MediaDeviceFailure.TypeError:
            return createErrorStatus(MediaInputStatusFlag.InvalidConstraints, requestedOrBoth);
        case MediaDeviceFailure.NotSupportedError:
            return createErrorStatus(MediaInputStatusFlag.NotSupported, requestedOrBoth);
        default:
            return createErrorStatus(MediaInputStatusFlag.UnknownError, requestedOrBoth);
    }
};
export const mergeStatusByConstraints = (prevStatus, nextStatus, constraints) => createUserMediaStatus({
    audio: constraints.audio === undefined || constraints.audio === false
        ? prevStatus.audio
        : nextStatus.audio,
    video: constraints.video === undefined || constraints.video === false
        ? prevStatus.video
        : nextStatus.video,
});
export const mergeMediaConstraints = (previous, next) => ({
    ...previous,
    ...(next.audio === undefined ? {} : { audio: next.audio }),
    ...(next.video === undefined ? {} : { video: next.video }),
});
export const mergePermissionStatus = (status, permission) => {
    const permissionStatus = getPermissionStatus(permission);
    const mergeInput = (kind) => status[kind] & InputStatusMask.Permission
        ? status[kind]
        : status[kind] | permissionStatus[kind];
    return createUserMediaStatus({
        audio: mergeInput('audio'),
        video: mergeInput('video'),
    });
};
export const mergeInputErrorStatus = (status, errorStatus, kind) => {
    let nextStatus = replaceInputStatus(status, kind, InputStatusMask.Error, errorStatus[kind] & InputStatusMask.Error);
    const permission = errorStatus[kind] & InputStatusMask.Permission;
    if (permission) {
        nextStatus = replaceInputStatus(nextStatus, kind, InputStatusMask.Permission, permission);
    }
    return nextStatus;
};
/**
 * Try to come up with an error level according to provided UserMediaStatus and
 * MediaDeviceRequest
 *
 * @param error - The error thrown from media request
 * @param status - The status in result
 * @param constraints - The constraints used for the request
 */
export const deriveErrorLevel = (error, status, constraints) => {
    const failure = getMediaDeviceFailure(error);
    const invalidOrUnsupported = failure === MediaDeviceFailure.TypeError ||
        failure === MediaDeviceFailure.NotSupportedError;
    const unexpectedOverconstrained = failure === MediaDeviceFailure.OverconstrainedError &&
        ![constraints.audio, constraints.video].some(constraint => isExactDeviceConstraint(constraint));
    return invalidOrUnsupported ||
        unexpectedOverconstrained ||
        isUnknownError(status)
        ? 'error'
        : 'warn';
};
export const requestUserMedia = (getCurrentDevices, getMedia = getUserMedia) => async (constraints, signal) => {
    try {
        const stream = await getMedia(constraints);
        const devices = await getCurrentDevices(() => !signal?.aborted);
        const streamedRequestedDevices = isStreamingRequestedDevices(constraints, stream, devices);
        const status = deriveSuccessStatus(constraints, streamedRequestedDevices, devices);
        return [stream, status];
    }
    catch (error) {
        if (error instanceof Error) {
            return [undefined, deriveErrorStatus(error, constraints)];
        }
        throw error;
    }
};
export const requestUserMediaWithRetry = (getCurrentDevices, createRequestUserMedia = requestUserMedia, gUM = getUserMedia) => {
    const request = createRequestUserMedia(getCurrentDevices, gUM);
    return async (constraints, signal) => {
        const initialResult = await request(constraints, signal);
        if (signal?.aborted ||
            initialResult[0] ||
            !constraints.audio ||
            !constraints.video) {
            return initialResult;
        }
        const guardResult = async (result) => {
            const media = await result;
            let stopped = false;
            const stopTracks = () => {
                if (stopped) {
                    return;
                }
                stopped = true;
                for (const track of media[0]?.getTracks() ?? []) {
                    track.stop();
                }
            };
            if (signal?.aborted) {
                stopTracks();
            }
            else {
                signal?.addEventListener('abort', stopTracks, { once: true });
            }
            return { media, stopTracks };
        };
        const [audioGuard, videoGuard] = await Promise.all([
            guardResult(request({ audio: constraints.audio }, signal)),
            guardResult(request({ video: constraints.video }, signal)),
        ]);
        signal?.removeEventListener('abort', audioGuard.stopTracks);
        signal?.removeEventListener('abort', videoGuard.stopTracks);
        const audioResult = audioGuard.media;
        const videoResult = videoGuard.media;
        const [audioStream, audioStatus] = audioResult;
        const [videoStream, videoStatus] = videoResult;
        const status = createUserMediaStatus({
            audio: audioStatus.audio,
            video: videoStatus.video,
        });
        if (signal?.aborted) {
            audioGuard.stopTracks();
            videoGuard.stopTracks();
            return [undefined, status];
        }
        const tracks = [
            ...(audioStream?.getAudioTracks() ?? []),
            ...(videoStream?.getVideoTracks() ?? []),
        ];
        const stream = audioStream && videoStream
            ? new MediaStream(tracks)
            : (audioStream ?? videoStream);
        return [stream, status];
    };
};
/**
 * A process to get user media
 */
export const createGetUserMediaProcess = ({ getCurrentDevices, getUserMedia, scope = 'media', signals, stopVideoTrackAsMute, updateMedia, }) => {
    return async ({ constraints, permission, originalConstraints, currentMedia, isCurrent = () => true, signal, }) => {
        const assertCurrent = (stream) => {
            if (isCurrent()) {
                return;
            }
            for (const track of stream?.getTracks() ?? []) {
                track.stop();
            }
            throw new Error('Stale media request');
        };
        const getOriginalConstraints = () => currentMedia
            ? mergeMediaConstraints(currentMedia.getOriginalConstraints(), originalConstraints)
            : originalConstraints;
        if (currentMedia) {
            const tracksToRelease = currentMedia.getTracks().filter(track => {
                const constraint = track.kind === 'audioinput'
                    ? constraints.audio
                    : track.kind === 'videoinput'
                        ? constraints.video
                        : undefined;
                assert(track.kind === 'audioinput' || track.kind === 'videoinput', `Unexpected track kind: ${track.kind}`);
                return constraint !== undefined;
            });
            await Promise.all(tracksToRelease.map(async (track) => {
                await track.release(isCurrent);
                if (isCurrent()) {
                    currentMedia.removeTrack(track);
                }
            }));
            assertCurrent();
        }
        const currentDevices = await getCurrentDevices(isCurrent);
        assertCurrent();
        logger.debug({
            scope,
            originalConstraints,
            constraints,
            currentDevices,
            currentMedia,
        }, 'pre getUserMedia');
        if (!constraints.audio && !constraints.video) {
            currentMedia?.setOriginalConstraints(getOriginalConstraints());
            if (currentMedia) {
                const permissionStatus = getPermissionStatus(permission);
                currentMedia.status = createUserMediaStatus({
                    audio: constraints.audio === false
                        ? permissionStatus.audio
                        : currentMedia.status.audio,
                    video: constraints.video === false
                        ? permissionStatus.video
                        : currentMedia.status.video,
                });
            }
            return (currentMedia ??
                buildMedia({
                    constraints,
                    permission,
                    originalConstraints: getOriginalConstraints(),
                    devices: currentDevices,
                    status: getPermissionStatus(permission),
                    stream: undefined,
                    signals,
                    tracks: [],
                }));
        }
        const relaxedConstraints = {};
        if (constraints.audio !== undefined) {
            relaxedConstraints.audio = relaxInputConstraint('audioinput', constraints.audio, currentDevices);
        }
        if (constraints.video !== undefined) {
            relaxedConstraints.video = relaxInputConstraint('videoinput', constraints.video, currentDevices);
        }
        const [stream, status] = await getUserMedia(relaxedConstraints, signal);
        assertCurrent(stream);
        const devices = await getCurrentDevices(isCurrent);
        assertCurrent(stream);
        const statusWithPermission = mergePermissionStatus(status, permission);
        let finalStatus = statusWithPermission;
        if (currentMedia) {
            finalStatus = mergeStatusByConstraints(currentMedia.status, statusWithPermission, constraints);
        }
        logger.debug({
            scope,
            stream,
            devices,
            status: formatUserMediaStatus(finalStatus),
        }, 'post getUserMedia');
        const findInput = findMediaInputFromMediaStreamTrack(devices);
        let audioMediaTrack;
        let videoMediaTrack;
        const extractContentHint = extractConstraintsWithKeys(['contentHint']);
        let trackReleaseTimeoutID = 0;
        /**
         * Release the track as mute
         */
        const releaseTrackAsMute = async (track, toMute, soft) => {
            if (!track?.track || track.muted === toMute) {
                return;
            }
            track.track.enabled = !toMute;
            if (soft) {
                return;
            }
            if (toMute) {
                if (!track.stopped) {
                    // Track cannot be stopped immediately as we need to ensure
                    // the last frame is black to avoid frozen frame in a gateway call
                    trackReleaseTimeoutID = window.setTimeout(() => {
                        track.release();
                        trackReleaseTimeoutID = 0;
                    }, 500);
                }
            }
            else {
                if (trackReleaseTimeoutID) {
                    window.clearTimeout(trackReleaseTimeoutID);
                    trackReleaseTimeoutID = 0;
                }
                if (track.stopped) {
                    await updateMedia({
                        [track.kind === 'audioinput' ? 'audio' : 'video']: track.getConstraints(),
                    });
                }
            }
        };
        if (constraints.audio !== undefined) {
            const audioTrack = stream?.getAudioTracks().at(0);
            const audioInput = findInput(audioTrack);
            assert(Boolean(audioTrack) === Boolean(audioInput), 'Inconsistent audioTrack and audioInput');
            const { contentHint: [[contentHint] = []], } = extractContentHint(constraints.audio);
            if (audioTrack) {
                audioTrack.contentHint = contentHint ?? '';
            }
            audioMediaTrack = createMediaTrack({
                label: [scope, PROCESSOR_LABELS.GetUserMedia].join('|'),
                kind: 'audioinput',
                track: audioTrack,
                input: audioInput,
                expectedInput: findExpectedInput(devices, constraints.audio, audioInput, 'audioinput'),
                constraints: constraints.audio,
                signals,
            });
            currentMedia?.addTrack(audioMediaTrack);
        }
        if (constraints.video !== undefined) {
            const videoTrack = stream?.getVideoTracks().at(0);
            const videoInput = findInput(videoTrack);
            assert(Boolean(videoTrack) === Boolean(videoInput), 'Inconsistent videoTrack and videoInput');
            const { contentHint: [[contentHint] = []], } = extractContentHint(constraints.video);
            if (videoTrack) {
                videoTrack.contentHint = contentHint ?? '';
            }
            videoMediaTrack = createMediaTrack({
                label: [scope, PROCESSOR_LABELS.GetUserMedia].join('|'),
                kind: 'videoinput',
                track: videoTrack,
                input: videoInput,
                expectedInput: findExpectedInput(devices, constraints.video, videoInput, 'videoinput'),
                get overrideMute() {
                    return stopVideoTrackAsMute();
                },
                get muted() {
                    return !videoTrack?.enabled;
                },
                mute(toMute, self, soft) {
                    releaseTrackAsMute(self, toMute, soft);
                },
                constraints: constraints.video,
                signals,
            });
            currentMedia?.addTrack(videoMediaTrack);
        }
        logger.info({
            permission,
            devices,
            stream,
            status: formatUserMediaStatus(finalStatus),
            originalConstraints,
            currentMedia,
            audioMediaTrack,
            videoMediaTrack,
            constraints,
        }, 'GetUserMedia');
        if (currentMedia) {
            currentMedia.status = finalStatus;
            currentMedia.setOriginalConstraints(getOriginalConstraints());
            return currentMedia;
        }
        return buildMedia({
            constraints,
            permission,
            originalConstraints: getOriginalConstraints(),
            devices,
            stream,
            status: finalStatus,
            signals,
            tracks: [audioMediaTrack, videoMediaTrack].flatMap(track => track ? [track] : []),
        });
    };
};
//# sourceMappingURL=userMedia.js.map